import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import api from "@/lib/api";

// Each brand renders as a stylised gift-card visual using its real brand color palette.
// Two-tone gradient + subtle radial highlight + chip icon makes the cards feel tangible.
const BRAND_THEMES = {
  amazon:        ["#232F3E", "#FF9900"],
  walmart:       ["#0071CE", "#FFC220"],
  target:        ["#CC0000", "#7C0000"],
  bestbuy:       ["#0046BE", "#FFE000"],
  ebay:          ["#E53238", "#0064D2"],
  macys:         ["#E21A2C", "#000000"],
  kohls:         ["#7E2E84", "#000000"],
  jcpenney:      ["#CC0000", "#1A1A1A"],
  nordstrom:     ["#1A1A1A", "#444444"],
  neimanmarcus:  ["#7C0A02", "#000000"],
  bloomingdales: ["#000000", "#444444"],
  belk:          ["#3E5B8A", "#0B3C5D"],
  nike:          ["#111111", "#FA5400"],
  adidas:        ["#000000", "#444444"],
  hm:            ["#E50010", "#7E0008"],
  lululemon:     ["#D72029", "#5C0E13"],
  footlocker:    ["#000000", "#E11B22"],
  gap:           ["#002F6C", "#0046BE"],
  oldnavy:       ["#003478", "#FFCC00"],
  steam:         ["#1B2838", "#66C0F4"],
  playstation:   ["#003791", "#0070D1"],
  xbox:          ["#107C10", "#0E5C0E"],
  nintendo:      ["#E60012", "#9B000C"],
  razergold:     ["#0D5F26", "#00FF6A"],
  gamestop:      ["#A60028", "#7C001E"],
  apple:         ["#0B3C5D", "#1d1d1f"],
  googleplay:    ["#34A853", "#1A6334"],
  microsoft:     ["#0078D4", "#7FBA00"],
  verizon:       ["#CD040B", "#1A1A1A"],
  starbucks:     ["#00704A", "#003B25"],
  sephora:       ["#000000", "#444444"],
  ulta:          ["#F36F32", "#A93D0A"],
  bbw:           ["#0B3C5D", "#3E5B8A"],
  uber:          ["#000000", "#222222"],
  airbnb:        ["#FF5A5F", "#E11D48"],
  hotels:        ["#D32F2F", "#7C1818"],
  delta:         ["#003366", "#C8102E"],
  doordash:      ["#FF3008", "#A91D00"],
  visa:          ["#1A1F71", "#0E1248"],
  mastercard:    ["#EB001B", "#F79E1B"],
  amex:          ["#2E77BC", "#1A4674"],
  one4all:       ["#E11D48", "#9F0E2E"],
  onevanilla:    ["#0F766E", "#054C46"],
  saks:          ["#000000", "#444444"],
  cvs:           ["#CC0000", "#7C0000"],
  homedepot:     ["#F96302", "#A03E00"],
  dicks:         ["#005DA9", "#003B6B"],
  disney:        ["#0B3C5D", "#1A6BA8"],
};

function GiftCard({ brand, idx }) {
  const [c1, c2] = BRAND_THEMES[brand.id] || ["#0B3C5D", "#164e75"];
  const topRate = Math.round(Math.max(...Object.values(brand.rate)) * 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: Math.min(idx * 0.02, 0.4) }}
      className="group"
      data-testid={`brand-card-${brand.id}`}
    >
      <div
        className="relative aspect-[1.586/1] rounded-2xl overflow-hidden shadow-[0_8px_24px_rgba(11,60,93,0.12)] transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_18px_40px_rgba(11,60,93,0.18)]"
        style={{ background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)` }}
      >
        {/* glossy sheen */}
        <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/8 to-white/15 pointer-events-none" />
        {/* radial highlight */}
        <div
          className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-30 blur-2xl pointer-events-none"
          style={{ background: "#ffffff" }}
        />
        {/* faint stripe */}
        <div className="absolute inset-x-0 top-1/2 h-px bg-white/10" />

        {/* chip */}
        <div className="absolute top-3 left-3 w-7 h-5 rounded-[4px] bg-gradient-to-br from-yellow-200 to-yellow-500 border border-yellow-700/30 shadow-inner" />

        {/* rate badge */}
        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-white/15 backdrop-blur-sm text-[10px] font-semibold text-white border border-white/20">
          up to {topRate}%
        </div>

        {/* brand name */}
        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-[10px] uppercase tracking-[0.18em] text-white/60">Gift Card</p>
          <p className="font-display text-base sm:text-lg font-bold text-white leading-tight mt-0.5 line-clamp-2">
            {brand.name}
          </p>
        </div>

        {/* sparkle accent */}
        <Sparkles className="absolute bottom-3 right-3 w-3.5 h-3.5 text-white/40" strokeWidth={2} />
      </div>
      <p className="mt-2 text-[11px] text-slate-500 text-center font-medium">{brand.category}</p>
    </motion.div>
  );
}

export default function BrandsGrid() {
  const [brands, setBrands] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/catalog/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  const filtered = brands.filter((b) =>
    b.name.toLowerCase().includes(query.toLowerCase()) ||
    b.category.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <section className="py-20 bg-[#F8FAFC]" data-testid="brands-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#00C853] font-semibold">Supported Brands</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B3C5D] mt-3 tracking-tight">
              45+ gift cards. Real rates.
            </h2>
            <p className="mt-3 text-slate-600 max-w-xl">
              From Amazon to Apple, Steam to Sephora — tap any card to start a sale.
            </p>
          </div>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search brand or category..."
            className="rounded-full border border-slate-200 px-5 py-3 text-sm bg-white focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10 w-full md:w-80"
            data-testid="brands-search"
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-5">
          {filtered.map((b, idx) => (
            <GiftCard key={b.id} brand={b} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}
